// useArtifactJson<T>(id, artifact?, { enabled? }) → UseQueryResult<T>     key ["artifact-json", id, sha]
// A JSON artifact fetched same-origin (/v1/jobs/{id}/artifacts/{sha}, never S3: CORS) and kept:
// it is content-addressed, so it never goes stale.
//   useStaticReport(id, artifacts, opts?)   → StaticReport   (ready before detonation)
//   useBehaviorReport(id, artifacts, opts?) → BehaviorReport (authoritative after the end: pass { enabled: ended })
//   useConfigReport(id, artifacts, opts?)   → ConfigReport   (written at teardown by the config extractors)
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Artifact, BehaviorReport, ConfigReport, StaticReport } from "@/types";
import { keys } from "./keys";
import { pickArtifact } from "./useArtifacts";

function useArtifactJson<T>(id: string, artifact: Pick<Artifact, "sha256"> | undefined, opts: { enabled?: boolean } = {}) {
  const sha = artifact?.sha256 ?? "";
  return useQuery({
    queryKey: keys.artifactJson(id, sha),
    queryFn: () => api.artifactJson<T>(id, sha),
    enabled: !!id && !!sha && (opts.enabled ?? true),
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    retry: 1,
  });
}

export function useStaticReport(id: string, artifacts: readonly Artifact[] | undefined, opts?: { enabled?: boolean }) {
  return useArtifactJson<StaticReport>(id, pickArtifact(artifacts, "static-report"), opts);
}

export function useConfigReport(id: string, artifacts: readonly Artifact[] | undefined, opts?: { enabled?: boolean }) {
  return useArtifactJson<ConfigReport>(id, pickArtifact(artifacts, "config-report"), opts);
}

export function useBehaviorReport(id: string, artifacts: readonly Artifact[] | undefined, opts?: { enabled?: boolean }) {
  return useArtifactJson<BehaviorReport>(id, pickArtifact(artifacts, "behavior-report"), opts);
}
